import React, { useRef, useState, forwardRef, useImperativeHandle, useLayoutEffect, useEffect } from 'react'

export const EditorViewport = forwardRef(function EditorViewport ({
  url,
  imageDims,
  zoom,
  setZoom,
  linkPanels,
  setLinkPanels,
  currentDateKey,
  onPageDimsDetected,
  onSelectionCommit
}, ref) {
  const containerRef = useRef(null)
  const iframeRef = useRef(null)
  const [isViewportDragging, setIsViewportDragging] = useState(false)
  const [isBoxResizing, setIsBoxResizing] = useState(false)
  const [isBoxDragging, setIsBoxDragging] = useState(false)
  const [resizeType, setResizeType] = useState('')
  const [draggedPanelIndex, setDraggedPanelIndex] = useState(null)
  const viewportDragStart = useRef(null)
  const boxDragStart = useRef(null)
  // Track whether a box was actually moved/resized during the drag
  const boxWasModified = useRef(false)
  /** Wheel-zoom: apply scroll correction in useLayoutEffect after DOM matches new zoom (avoids setTimeout jitter). */
  const wheelScrollPendingRef = useRef(null)
  const zoomRef = useRef(zoom)
  const imageDimsRef = useRef(imageDims)
  const setZoomRef = useRef(setZoom)
  zoomRef.current = zoom
  imageDimsRef.current = imageDims
  setZoomRef.current = setZoom

  const getCurrentSelection = (panel) => panel.selectionsByDate[currentDateKey]

  const isZeroSelection = (sel) =>
    sel && sel.left === 0 && sel.top === 0 && sel.width === 0 && sel.height === 0

  const updateCurrentSelection = (panel, selection) => ({
    ...panel,
    selectionsByDate: { ...panel.selectionsByDate, [currentDateKey]: selection }
  })

  useImperativeHandle(ref, () => ({
    getVisibleArea: () => {
      const container = containerRef.current
      if (!container || !imageDims) return { left: 0, top: 0, width: 100, height: 100 }
      const rect = container.getBoundingClientRect()
      return {
        left: container.scrollLeft / zoom,
        top: container.scrollTop / zoom,
        width: rect.width / zoom,
        height: rect.height / zoom
      }
    },
    zoomToSelection: (selection) => {
      const container = containerRef.current
      if (!container || !imageDims) return
      const rect = container.getBoundingClientRect()
      const PADDING = 40
      const targetZoom = Math.min(
        rect.width / (selection.width + PADDING * 2),
        rect.height / (selection.height + PADDING * 2),
        4
      )
      const newZoom = Math.max(0.2, targetZoom)
      const selCenterX = selection.left + selection.width / 2
      const selCenterY = selection.top + selection.height / 2
      setZoom(newZoom)
      setTimeout(() => {
        if (!containerRef.current) return
        containerRef.current.scrollLeft = selCenterX * newZoom - rect.width / 2
        containerRef.current.scrollTop = selCenterY * newZoom - rect.height / 2
      }, 0)
    },
    getTextAtSelection: (sel) => {
      try {
        const iframe = iframeRef.current
        const doc = iframe?.contentDocument
        const win = iframe?.contentWindow
        if (!doc) return ''
        // Document coordinates → iframe viewport (elementsFromPoint is viewport-relative)
        const scrollX = win ? (win.scrollX || win.pageXOffset || 0) : 0
        const scrollY = win ? (win.scrollY || win.pageYOffset || 0) : 0
        const cx = sel.left + sel.width / 2 - scrollX
        const cy = sel.top + sel.height / 2 - scrollY
        const els = doc.elementsFromPoint(cx, cy)
        for (const el of els) {
          // Skip html/body/root containers
          if (el === doc.documentElement || el === doc.body) continue
          const text = el.textContent?.trim()
          if (text && text.length > 0 && text.length < 200) return text
        }
        return ''
      } catch (_) {
        return ''
      }
    },

    captureSummaryLayoutCalibration: async () => {
      const iframe = iframeRef.current
      const doc = iframe?.contentDocument
      const win = iframe?.contentWindow
      if (!doc || !win) {
        return { error: 'Preview iframe is not available.' }
      }
      if (!doc.querySelector('.wv-summary-panel')) {
        return {
          error:
            'Open the Summary page in the preview (URL must include /website/summary), wait for it to load, then try again.'
        }
      }
      try {
        if (doc.fonts && doc.fonts.ready) await doc.fonts.ready
      } catch (_) {}

      const elHtml = doc.documentElement
      const body = doc.body
      const fullWidth = Math.max(elHtml.scrollWidth, body?.scrollWidth ?? 0)
      const fullHeight = Math.max(elHtml.scrollHeight, body?.scrollHeight ?? 0)
      if (fullWidth < 10 || fullHeight < 10) {
        return { error: 'Could not read document dimensions from the preview.' }
      }
      if (
        imageDims &&
        (Math.abs(fullWidth - imageDims.width) > 2 ||
          Math.abs(fullHeight - imageDims.height) > 2)
      ) {
        return {
          error: `Preview document size (${fullWidth}×${fullHeight}) does not match the editor layout (${imageDims.width}×${imageDims.height}). Scroll the preview to the top and reload the Summary page, then try again.`
        }
      }

      function docRect (element) {
        if (!element) return null
        const r = element.getBoundingClientRect()
        const sx = win.scrollX || win.pageXOffset || 0
        const sy = win.scrollY || win.pageYOffset || 0
        return {
          left: r.left + sx,
          top: r.top + sy,
          width: r.width,
          height: r.height
        }
      }

      const regions = []

      function pushStatic (element, textOverride) {
        const r = docRect(element)
        if (!r || r.width < 1 || r.height < 1) return
        regions.push({
          kind: 'static',
          left: r.left,
          top: r.top,
          width: r.width,
          height: r.height,
          text: textOverride != null ? String(textOverride) : (element.textContent || '').trim()
        })
      }

      const pageTitle = doc.querySelector('h1.wv-title')
      if (pageTitle) pushStatic(pageTitle)

      doc.querySelectorAll('nav.wv-nav a').forEach((a) => pushStatic(a))

      const sectionTitle = doc.querySelector('.wv-summary-panel .wv-section-title')
      if (sectionTitle) pushStatic(sectionTitle)

      const statColumns = doc.querySelectorAll('.wv-summary-stats > div')
      statColumns.forEach((col, idx) => {
        const labelEl = col.querySelector('.wv-stat-label')
        const valueEl = col.querySelector('.wv-stat-value')
        if (labelEl) pushStatic(labelEl)
        if (valueEl) {
          const r = docRect(valueEl)
          if (r && r.width >= 1 && r.height >= 1) {
            regions.push({
              kind: 'stat',
              field: idx === 0 ? 'totalVotes' : 'uniqueVoters',
              left: r.left,
              top: r.top,
              width: r.width,
              height: r.height
            })
          }
        }
      })

      const table = doc.querySelector('table.wv-table')
      if (table) {
        table.querySelectorAll('thead tr th').forEach((th, col) => {
          const r = docRect(th)
          if (r && r.width >= 1 && r.height >= 1) {
            regions.push({
              kind: 'tableHeader',
              col,
              left: r.left,
              top: r.top,
              width: r.width,
              height: r.height,
              text: (th.textContent || '').trim()
            })
          }
        })
        table.querySelectorAll('tbody tr').forEach((tr, row) => {
          tr.querySelectorAll('td').forEach((td, col) => {
            if (col > 3) return
            const r = docRect(td)
            if (r && r.width >= 1 && r.height >= 1) {
              regions.push({
                kind: 'tableBody',
                row,
                col,
                left: r.left,
                top: r.top,
                width: r.width,
                height: r.height
              })
            }
          })
        })
      }

      if (regions.length < 1) {
        return { error: 'No measurable regions were found on the Summary page.' }
      }

      return {
        calibration: {
          version: 2,
          capturedAt: new Date().toISOString(),
          viewport: { width: fullWidth, height: fullHeight },
          regions
        }
      }
    }
  }), [zoom, imageDims, setZoom])

  useLayoutEffect(() => {
    const pending = wheelScrollPendingRef.current
    if (!pending || !containerRef.current) return
    wheelScrollPendingRef.current = null
    const c = containerRef.current
    const { contentX, contentY, mouseX, mouseY, oldL, oldT } = pending
    c.scrollLeft = contentX * zoom - (mouseX - oldL)
    c.scrollTop = contentY * zoom - (mouseY - oldT)
  }, [zoom])

  /** Non-passive wheel so preventDefault stops scroll chaining to the embedding page (iframe parent). */
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) return
      e.preventDefault()
      const dims = imageDimsRef.current
      const z = zoomRef.current
      const container = containerRef.current
      if (!container || !dims) return
      const rect = container.getBoundingClientRect()
      const oldL = container.scrollLeft
      const oldT = container.scrollTop
      const mouseX = e.clientX - rect.left + oldL
      const mouseY = e.clientY - rect.top + oldT
      const newZoom = Math.max(0.2, Math.min(5, z - e.deltaY * 0.001))
      if (newZoom === z) return
      wheelScrollPendingRef.current = {
        contentX: mouseX / z,
        contentY: mouseY / z,
        mouseX,
        mouseY,
        oldL,
        oldT
      }
      setZoomRef.current(newZoom)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  function handleIframeLoad (e) {
    try {
      const doc = e.target.contentDocument
      if (!doc) return
      const el = doc.documentElement
      const body = doc.body
      const fullWidth = Math.max(el.scrollWidth, body?.scrollWidth ?? 0)
      const fullHeight = Math.max(el.scrollHeight, body?.scrollHeight ?? 0)
      if (fullHeight > 0 && fullWidth > 0 && onPageDimsDetected) {
        onPageDimsDetected({ width: fullWidth, height: fullHeight })
      }
    } catch (_) {
      // Cross-origin or load error — keep current dims
    }
  }

  function handleMouseDown (e) {
    if (!containerRef.current) return
    e.preventDefault()
    setIsViewportDragging(true)
    viewportDragStart.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: containerRef.current.scrollLeft,
      scrollTop: containerRef.current.scrollTop
    }
    document.body.style.userSelect = 'none'
  }

  function handleMouseMove (e) {
    if (boxDragStart.current && draggedPanelIndex !== null) {
      handleBoxMouseMove(e)
      return
    }
    if (!isViewportDragging || !containerRef.current || !viewportDragStart.current) return
    const dx = e.clientX - viewportDragStart.current.x
    const dy = e.clientY - viewportDragStart.current.y
    containerRef.current.scrollLeft = viewportDragStart.current.scrollLeft - dx
    containerRef.current.scrollTop = viewportDragStart.current.scrollTop - dy
  }

  function handleMouseUpOrLeave () {
    const wasBoxModified = boxWasModified.current
    const committedPanelIndex = draggedPanelIndex

    setIsViewportDragging(false)
    setIsBoxResizing(false)
    setIsBoxDragging(false)
    setResizeType('')
    setDraggedPanelIndex(null)
    viewportDragStart.current = null
    boxDragStart.current = null
    boxWasModified.current = false
    document.body.style.userSelect = ''

    // Fire OCR callback after state is cleaned up
    if (wasBoxModified && committedPanelIndex !== null && onSelectionCommit) {
      onSelectionCommit(committedPanelIndex)
    }
  }

  function handleBoxMouseDown (e, panelIndex) {
    e.stopPropagation()
    e.preventDefault()
    const target = e.target
    const isHandle = target.classList.contains('resize-handle')
    if (isHandle) {
      setIsBoxResizing(true)
      setResizeType(target.classList[1])
    } else {
      setIsBoxDragging(true)
    }
    setDraggedPanelIndex(panelIndex)
    boxWasModified.current = false
    const panel = linkPanels[panelIndex]
    const currentSelection = getCurrentSelection(panel)
    if (!currentSelection || isZeroSelection(currentSelection)) return
    boxDragStart.current = {
      x: e.clientX,
      y: e.clientY,
      boxLeft: currentSelection.left,
      boxTop: currentSelection.top,
      boxWidth: currentSelection.width,
      boxHeight: currentSelection.height
    }
    document.body.style.userSelect = 'none'
  }

  function handleBoxMouseMove (e) {
    if (!boxDragStart.current || draggedPanelIndex === null) return
    const deltaX = (e.clientX - boxDragStart.current.x) / zoom
    const deltaY = (e.clientY - boxDragStart.current.y) / zoom
    const updatedPanels = [...linkPanels]
    const panel = updatedPanels[draggedPanelIndex]

    if (isBoxResizing && resizeType) {
      let newLeft = boxDragStart.current.boxLeft
      let newTop = boxDragStart.current.boxTop
      let newWidth = boxDragStart.current.boxWidth
      let newHeight = boxDragStart.current.boxHeight
      switch (resizeType) {
        case 'nw': newLeft += deltaX; newTop += deltaY; newWidth -= deltaX; newHeight -= deltaY; break
        case 'ne': newTop += deltaY; newWidth += deltaX; newHeight -= deltaY; break
        case 'sw': newLeft += deltaX; newWidth -= deltaX; newHeight += deltaY; break
        case 'se': newWidth += deltaX; newHeight += deltaY; break
        case 'n': newTop += deltaY; newHeight -= deltaY; break
        case 's': newHeight += deltaY; break
        case 'w': newLeft += deltaX; newWidth -= deltaX; break
        case 'e': newWidth += deltaX; break
        default: break
      }
      if (newWidth < 20) newWidth = 20
      if (newHeight < 20) newHeight = 20
      if (newLeft < 0) { newWidth += newLeft; newLeft = 0 }
      if (newTop < 0) { newHeight += newTop; newTop = 0 }
      if (newLeft + newWidth > imageDims.width) newWidth = imageDims.width - newLeft
      if (newTop + newHeight > imageDims.height) newHeight = imageDims.height - newTop
      updatedPanels[draggedPanelIndex] = updateCurrentSelection(panel, { left: newLeft, top: newTop, width: newWidth, height: newHeight })
    } else {
      const currentSelection = getCurrentSelection(panel)
      if (!currentSelection || isZeroSelection(currentSelection)) return
      let newLeft = Math.max(0, Math.min(imageDims.width - boxDragStart.current.boxWidth, boxDragStart.current.boxLeft + deltaX))
      let newTop = Math.max(0, Math.min(imageDims.height - boxDragStart.current.boxHeight, boxDragStart.current.boxTop + deltaY))
      updatedPanels[draggedPanelIndex] = updateCurrentSelection(panel, { left: newLeft, top: newTop, width: currentSelection.width, height: currentSelection.height })
    }
    boxWasModified.current = true
    setLinkPanels(updatedPanels)
  }

  const getViewportBorderColor = () => {
    for (const panel of linkPanels) {
      const sel = getCurrentSelection(panel)
      if (isZeroSelection(sel)) return panel.color
    }
    return null
  }

  const viewportBorderColor = getViewportBorderColor()

  return (
    <div
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUpOrLeave}
      onMouseLeave={handleMouseUpOrLeave}
      className='editor-viewport'
      style={{
        outline: viewportBorderColor ? `4px solid ${viewportBorderColor}` : undefined,
        outlineOffset: viewportBorderColor ? '-1px' : undefined
      }}
    >
      <div
        className='editor-viewport-content'
        style={{
          width: imageDims.width * zoom,
          height: imageDims.height * zoom,
          transform: `scale(${zoom})`,
          transformOrigin: 'top left',
          position: 'relative'
        }}
      >
        {/* Transparent overlay to capture mouse events above the iframe */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            cursor: isViewportDragging ? 'grabbing' : 'grab'
          }}
        />
        <iframe
          ref={iframeRef}
          src={url}
          title='Website preview'
          onLoad={handleIframeLoad}
          style={{
            width: imageDims.width,
            height: imageDims.height,
            border: 'none',
            display: 'block',
            pointerEvents: 'none'
          }}
          sandbox='allow-same-origin allow-scripts'
        />
        {linkPanels.map((panel, idx) => {
          const sel = getCurrentSelection(panel)
          if (!sel || isZeroSelection(sel)) return null
          return (
            <div
              key={idx}
              className='editor-selection-box'
              style={{
                left: sel.left,
                top: sel.top,
                width: sel.width,
                height: sel.height,
                border: `3px solid ${panel.color}`,
                zIndex: 3
              }}
              onMouseDown={(e) => handleBoxMouseDown(e, idx)}
            >
              <div className='resize-handle nw' />
              <div className='resize-handle ne' />
              <div className='resize-handle sw' />
              <div className='resize-handle se' />
              <div className='resize-handle n' />
              <div className='resize-handle s' />
              <div className='resize-handle w' />
              <div className='resize-handle e' />
            </div>
          )
        })}
      </div>
    </div>
  )
})
